import React, { useEffect, useState, useRef } from "react";
import { BsFolder2Open, BsQuestionCircle } from "react-icons/bs";
import {
  IoLogOutOutline,
  IoClose,
  IoMenu,
  IoPencil,
  IoCheckmark,
  IoSettingsOutline,
  IoBulbOutline,
  IoTrashOutline,
  IoDocumentTextOutline,
} from "react-icons/io5";
import { DataState } from "../context/DataProvider";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { API_URL } from "../services/api";
import { pages } from "@microsoft/teams-js";
import { useTeams } from "../context/TeamsProvider";
import ProfileSettingsModal from "./ProfileSettingsModal";

const TEAMS_APP_ID = process.env.REACT_APP_TEAMS_APP_ID;

const Sidebar = ({ toggleSidebar, isOpen, onToggle }) => {
  const { isTeamsInitialized } = useTeams();
  const {
    sendTaskLoading,
    setFeedbackModalOpen,
    tasks,
    setTasks,
    currentTaskId,
    setCurrentTaskId,
    setTools,
    loadConversations,
    handleCreateTask,
    setTheme,
    setJustChangedLocalStorageData,
    setSelectedReason,
    setIsReasoning,
    setIsWebSearch,
    setIsCanvas,
    setIsAgentMode,
    setAgentSequence,
    userInfo,
    setTaskMCPConnections,
    setTaskOqlousTools,
    setTaskManualToolSelection,
    setCurrentTaskModel,
    handleDeleteTask,
  } = DataState();

  const [openSettings, setOpenSettings] = useState(false);
  const sidebarRef = useRef(null);
  const navigate = useNavigate();

  // --- STATE FOR EDITING ---
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);
  const editInputRef = useRef(null);

  // --- STATE FOR DELETE CONFIRMATION ---
  const [deletingTaskId, setDeletingTaskId] = useState(null);

  // Focus input when editing starts
  useEffect(() => {
    if (editingTaskId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingTaskId]);

  const handleLogout = () => {
    async function logout() {
      try {
        await fetch(`${API_URL}/logout`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });
      } catch (error) {
        console.error("Network error during logout:", error);
      } finally {
        localStorage.clear();
        setJustChangedLocalStorageData(true);
        navigate("/login");
      }
    }
    logout();
  };

  useEffect(() => {
    const storedUserGoogleId = localStorage.getItem("projectToken");
    if (!storedUserGoogleId) {
      navigate("/login");
    }
  }, [navigate]);

  const handleProjectsButtonClick = () => {
    if (!isTeamsInitialized) {
      navigate("/projects");
      return;
    }
    const targetPageId = "projects";
    if (pages.currentApp.isSupported()) {
      pages.currentApp.navigateTo({ pageId: targetPageId }).catch(console.error);
    } else {
      pages.navigateToApp({ appId: TEAMS_APP_ID, pageId: targetPageId }).catch(console.error);
    }
  };

  const handleTaskSelect = (task) => {
    setCurrentTaskId(task._id);
    setSelectedReason(task.reasoning_effort || null);
    setIsWebSearch(task.is_web_search || false);
    setIsReasoning(task.is_reasoning || false);
    setIsCanvas(task.is_canvas || false);
    setIsAgentMode(task.is_agent_mode || false);
    setAgentSequence(task.agent_sequence || ["Orchestrator"]);
    setTaskMCPConnections(task.mcp_connections || []);
    setTaskOqlousTools(task.oqlous_tools || []);
    setTaskManualToolSelection(task.manual_tool_selection || false);
    setCurrentTaskModel(task.task_model || null);
    loadConversations(task._id);
    setTools(tasks.find((task1) => task1._id === task._id)?.tools || []);

    if (window.innerWidth < 768 && onToggle) {
      onToggle();
    }
  };

  // --- NEW: HANDLE RENAME ---
  const startEditing = (e, task) => {
    e.stopPropagation(); // Prevent selecting the task
    setEditingTaskId(task._id);
    setEditValue(task.task_description);
  };

  const cancelEditing = (e) => {
    if (e) e.stopPropagation();
    setEditingTaskId(null);
    setEditValue("");
  };

  const saveTaskName = async (e, taskId) => {
    if (e) e.stopPropagation();

    if (!editValue.trim() || editValue === tasks.find(t => t._id === taskId)?.task_description) {
      cancelEditing();
      return;
    }

    setIsRenaming(true);
    const token = localStorage.getItem("projectToken");

    try {
      const response = await fetch(`${API_URL}/tasks/${taskId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ task_description: editValue }),
      });

      const data = await response.json();

      if (response.ok) {
        // Update local state immediately to reflect change
        if (setTasks) {
          setTasks(prevTasks => {
            const taskIndex = prevTasks.findIndex(t => t._id === taskId);
            if (taskIndex === -1) return prevTasks;

            const updatedTask = { ...prevTasks[taskIndex], task_description: editValue };

            const otherTasks = prevTasks.filter((_, idx) => idx !== taskIndex);
            return [updatedTask, ...otherTasks];
          });
        }
        toast.success("Agent renamed");
      } else {
        toast.error(data.error || "Failed to rename");
      }
    } catch (error) {
      console.error("Rename error:", error);
      toast.error("Network error occurred");
    } finally {
      setIsRenaming(false);
      setEditingTaskId(null);
    }
  };

  const handleKeyDown = (e, taskId) => {
    if (e.key === "Enter") {
      saveTaskName(e, taskId);
    } else if (e.key === "Escape") {
      cancelEditing(e);
    }
  };

  // --- DELETE HANDLERS ---
  const confirmDelete = async (e, taskId) => {
    e.stopPropagation();
    await handleDeleteTask(taskId);
    setDeletingTaskId(null);
    toast.success("Chat deleted");
  };

  const cancelDelete = (e) => {
    if (e) e.stopPropagation();
    setDeletingTaskId(null);
  };

  // Close sidebar logic...
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && window.innerWidth < 768) {
        const sidebar = sidebarRef.current;
        const menuButton = document.querySelector('[aria-label="Toggle menu"]');
        if (
          sidebar &&
          !sidebar.contains(event.target) &&
          menuButton &&
          !menuButton.contains(event.target) &&
          !event.target.closest('.modal, [role="dialog"]') &&
          !openSettings
        ) {
          onToggle && onToggle();
        }
      }
    };

    if (isOpen) {
      // Add delay to prevent immediate closure
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, onToggle, openSettings]);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isOpen && window.innerWidth < 768) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen]);

  const sidebarContent = (
    <div className="flex flex-col justify-between h-full gap-1 overflow-hidden rounded-none bg-1 md:rounded-2xl">
      {/* Header Section */}
      <div className="relative flex flex-col items-center gap-0 px-3 py-0 md:px-2">
        {/* Mobile Header with Close Button */}
        <div className="flex items-center justify-between w-full mb-2 md:hidden">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onToggle && onToggle();
            }}
            className="absolute z-50 p-2 transition-colors rounded-lg right-1 top-1 hover:bg-white hover:bg-opacity-10"
          >
            <IoClose className="text-xl" />
          </button>
        </div>

        {/* Logo */}
        {/* {userInfo?.partnerLogo ? (
          <img
            src={`../assets/partners/${userInfo.partnerLogo}`}
            alt="Logo"
            className="object-contain w-full h-16 text-center md:h-18"
          />
        ) : (
          <img
            src={`../assets/logo.png`}
            alt="Logo"
            className="w-full h-8 mb-2 text-center md:mb-4 md:h-10"
          />
        )} */}
        {/* Logo Container */}
        <div className="flex items-center justify-center w-full gap-3 mb-2 md:mb-4">
          
          {/* Main Logo (Always visible) - RESTORED COMMENT */}
          {/* <img
            src={`../assets/logo.svg`}
            alt="Main Logo"
            className="object-contain h-8 md:h-10"
          /> */}

          {/* Partner Logo (Conditionally renders if it exists) */}
          {userInfo?.partnerLogo && !toggleSidebar && (
            <>
              {/* Visual Separator for clarity - RESTORED COMMENT */}
              <div className="h-6 border-l border-white/30" aria-hidden="true"></div>

              <img
                src={`../assets/partners/${userInfo.partnerLogo}`}
                alt="Partner Logo"
                className="object-contain h-7 md:h-9"
              />
            </>
          )}
        </div>
        {/* <img
          src={theme === "light" ? "../assets/logo.png" : "../assets/logo.png"}
          alt="Logo"
          className="w-full h-8 mb-2 text-center md:mb-4 md:h-10"
        /> */}

        {/* Action Buttons */}
        <div className="flex flex-col w-full gap-3 md:gap-4">
          <button
            disabled={sendTaskLoading}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleCreateTask();
              if (window.innerWidth < 768 && onToggle) {
                setTimeout(() => onToggle(), 100);
              }
            }}
            className={`flex justify-center items-center gap-2 
              rounded-xl px-3 py-3 md:py-2 cursor-pointer bg-primary-gradient 
              text-white text-sm md:text-xs sm:text-sm transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:pointer-events-none 
              ${toggleSidebar && "font-bold text-xl"}`}
          >
            {toggleSidebar ? "+" : "New Chat"}
          </button>

          {!toggleSidebar && (
            <button
              className="flex items-center justify-between gap-1 px-2 text-sm font-semibold transition-colors md:text-xs hover:text-opacity-80"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              Recent Agents
            </button>
          )}
        </div>
      </div>

      {/* History Section */}
      {!toggleSidebar && (
        <div className="flex flex-col flex-1 gap-1 p-3 mx-2 -mt-3 overflow-y-auto hide-sidebar rounded-xl bg-2 md:mx-2">
          {/* Changed gap-2 to gap-1 above for tighter list */}
          {tasks?.length > 0 ? (
            tasks.map((task) => (
              <div

                key={task._id}
                className="relative group"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  // 1. Add a guard clause: If loading, do nothing. - RESTORED COMMENT
                  if (sendTaskLoading || editingTaskId === task._id) return;
                  handleTaskSelect(task);
                }}
              >
                <div
                  className={`${task._id === currentTaskId
                    ? "bg-white bg-opacity-15"
                    : "bg-white bg-opacity-5"
                    } flex items-center justify-between gap-2 rounded-lg px-3 py-1 md:py-1 transition-all duration-200 min-h-[30px]
                    ${sendTaskLoading
                      ? "opacity-80 pointer-events-none"
                      : "hover:bg-zinc-800 cursor-pointer"
                    }`}
                // Changed py-2 to py-1 and min-h-[36px] to min-h-[30px] above
                >
                  {/* --- EDIT MODE --- */}
                  {editingTaskId === task._id ? (
                    <div className="flex items-center w-full gap-2">
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, task._id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full text-sm text-white bg-transparent border-b border-[#5652CF] outline-none md:text-xs sm:text-sm focus:border-blue-400"
                      />
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => saveTaskName(e, task._id)}
                          className="p-1 text-green-400 hover:text-green-300"
                        >
                          <IoCheckmark />
                        </button>
                        <button
                          onClick={(e) => cancelEditing(e)}
                          className="p-1 text-red-400 hover:text-red-300"
                        >
                          <IoClose />
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* --- VIEW MODE --- */
                    <>
                      <p className="flex-1 overflow-hidden text-sm text-ellipsis whitespace-nowrap md:text-xs sm:text-sm">
                        {task.task_description}
                      </p>

                      {/* Edit Icon - Hidden by default, visible on group-hover */}
                      {!sendTaskLoading && (
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-zinc-800 absolute right-0 px-1">
                          <button
                            onClick={(e) => startEditing(e, task)}
                            className="p-1 text-gray-400 hover:text-amber-400"
                            title="Rename"
                          >
                            <IoPencil className="text-sm" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletingTaskId(task._id);
                            }}
                            className="p-1 text-gray-400 hover:text-red-400"
                            title="Delete"
                          >
                            <IoTrashOutline className="text-sm" />
                          </button>
                        </div>
                      )}

                      {/* Delete confirmation */}
                      {deletingTaskId === task._id && (
                        <div className="absolute inset-0 flex items-center justify-between px-3 bg-red-900 bg-opacity-90 rounded-lg z-10">
                          <span className="text-xs text-white">Delete?</span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => confirmDelete(e, task._id)}
                              className="p-1 text-green-400 hover:text-green-300"
                            >
                              <IoCheckmark />
                            </button>
                            <button
                              onClick={(e) => cancelDelete(e)}
                              className="p-1 text-red-400 hover:text-red-300"
                            >
                              <IoClose />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col gap-3 p-2 text-sm text-center">
              No recent chats
            </div>
          )}
        </div>
      )}

      {/* Bottom Actions */}
      <div className="flex flex-col w-full gap-1 px-3 py-3 text-sm border-t border-white md:px-2 border-opacity-10 md:border-none">

        {/* +++ Make the User Info section a button to open the modal +++ - RESTORED COMMENT */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpenSettings(true);
            // Close sidebar on mobile
            if (window.innerWidth < 768 && onToggle) {
              setTimeout(() => onToggle(), 100);
            }
          }}
          className={`w-full rounded-lg gap-3 flex ${toggleSidebar ? "justify-center" : "justify-start"
            } items-center p-3 md:p-2 hover:bg-white hover:bg-opacity-10 transition-all duration-200`}
        >
          {userInfo && (
            <div className="flex items-center gap-3">
              {userInfo.picture ? (
                <img
                  src={userInfo.picture}
                  alt={userInfo.name}
                  className="rounded-full h-[25px] w-[25px] object-cover"
                />
              ) : (
                <div className="rounded-full h-[25px] w-[25px] bg-gray-300 flex items-center justify-center text-sm font-bold text-gray-600">
                  {userInfo.name
                    ? userInfo.name?.charAt(0).toUpperCase()
                    : userInfo.email?.charAt(0).toUpperCase()}
                </div>
              )}
              <span
                className={`max-w-20 md:max-w-24 overflow-hidden text-ellipsis whitespace-nowrap text-sm ${toggleSidebar && "hidden"
                  }`}
              >
                {userInfo.name || userInfo.email}
              </span>
            </div>
          )}
        </button>

        {/* RESTORED: Commented out Settings Button */}
        {/* <button
          // onClick={(e) => {
          //   e.stopPropagation();
          //   e.preventDefault();
          //   setOpenSettings(true);
          // }}
          className={`rounded-lg gap-3 flex ${
            toggleSidebar ? "justify-center" : "justify-start"
          } items-center p-3 md:p-2 hover:bg-white hover:bg-opacity-10 transition-all duration-200`}
        >
          <IoSettingsOutline className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && (
            <span className="text-sm md:text-sm">Settings</span>
          )}
        </button> */}

        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            navigate("/report-templates");
            if (window.innerWidth < 768 && onToggle) {
              setTimeout(() => onToggle(), 100);
            }
          }}
          className={`rounded-lg gap-3 flex ${toggleSidebar ? "justify-center" : "justify-start"
            } items-center p-3 md:p-2 hover:bg-white hover:bg-opacity-10 transition-all duration-200`}
        >
          <IoDocumentTextOutline className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && <span className="text-sm md:text-sm">Report Templates</span>}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleProjectsButtonClick();
          }}
          className={`rounded-lg gap-3 flex ${toggleSidebar ? "justify-center" : "justify-start"
            } items-center p-3 md:p-2 hover:bg-white hover:bg-opacity-10 transition-all duration-200`}
        >
          <BsFolder2Open className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && <span className="text-sm md:text-sm">Projects</span>}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleLogout();
          }}
          className={`rounded-lg gap-3 flex ${toggleSidebar ? "justify-center" : "justify-start"
            } items-center p-3 md:p-2 hover:bg-white hover:bg-opacity-10 transition-all duration-200`}
        >
          <IoLogOutOutline className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && <span className="text-sm md:text-sm">Log out</span>}
        </button>

        {/* RESTORED: Commented out Feedback Button */}
        {/* <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setFeedbackModalOpen(true);
            // Close sidebar on mobile
            if (window.innerWidth < 768 && onToggle) {
              setTimeout(() => onToggle(), 100);
            }
          }}
          className={`rounded-lg gap-3 flex ${toggleSidebar ? "justify-center" : "justify-start"
            } items-center p-3 md:p-2 hover:bg-white hover:bg-opacity-10 transition-all duration-200`}
        >
          <IoBulbOutline className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && (
            <span className="text-sm md:text-sm">Feedback</span>
          )}
        </button> */}

        {/* RESTORED: Commented out Help Link */}
        {/* <a
          href="mailto:nirmalnambiar@oqlous.com"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            window.location.href = "mailto:nirmalnambiar@oqlous.com";
          }}
          className={`rounded-lg gap-3 flex ${toggleSidebar ? "justify-center" : "justify-start"
            } items-center p-3 md:p-2 hover:bg-white hover:bg-opacity-10 transition-all duration-200`}
        >
          <BsQuestionCircle className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && <span className="text-sm md:text-sm">Help</span>}
        </a> */}

      </div>

      {/* Settings Modal */}
      {/* +++ Render the new ProfileSettingsModal component +++ */}
      <ProfileSettingsModal
        isOpen={openSettings}
        onClose={() => setOpenSettings(false)}
        userInfo={userInfo}
      />

    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden h-full md:block">{sidebarContent}</div>

      {/* Mobile Sidebar Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 transition-opacity bg-black bg-opacity-50"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
          />

          {/* Sidebar - Increased width and added ref */}
          <div
            ref={sidebarRef}
            id="mobile-sidebar"
            className={`relative w-80 max-w-[70vw] h-full bg-gray-900 transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "-translate-x-full"
              }`}
            onClick={(e) => { e.stopPropagation(); }}
          >
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Mobile Menu Button - Only show when sidebar is closed */}
      {!isOpen && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onToggle();
          }}
          className="fixed z-40 p-2 transition-all duration-200 bg-opacity-50 rounded-lg md:hidden top-5 left-4 backdrop-blur-sm hover:bg-opacity-70"
          aria-label="Toggle menu"
        >
          <IoMenu className="text-2xl text-white" />
        </button>
      )}
    </>
  );
};

export default Sidebar;